import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {AppConfig} from '@mpt-fo/common/shared/config/app-config';
import {AuthenticationService} from '@mpt-fo/common/core/services/authentication.service';
import {LocalStorageService} from '@mpt-fo/common/core/services/local-storage.service';
import {AuthorizedUser} from '@mpt-fo/common/core/services/authorized-user.service';
import {DeviceService} from '@mpt-fo/common/core/services/device.service';
import {ComplianceService} from '@mpt-fo/common/core/services/compliance.service';
import {ModalOptionsConfig, ModalService} from '@mpt-fo/common/lean-shared/components/nz-modal-service/modal.service';
import {MobileWebViewService} from '@mpt-fo/common/core/services/mobile-web-view.service';
import {MobileDelegateService} from '@mpt-fo/common/core/services/mobile-delegate.service';
import {KycService} from '@mpt-fo/common/core/services/kyc-service';
import {LoginHelperService} from '@mpt-fo/common/core/services/login-helper.service';
import {ModalHelperService, ModalOptions} from '@mpt-fo/common/core/services/modal-helper.service';
import {DeviceHelperService} from '@mpt-fo/common/core/services/device-helper.service';
import {DataLayerService} from '@mpt-fo/common/core/services/data-layer-service.service';
import {MessageType} from '@mpt-fo/common/shared/models/utility.model';
import {MessageModalComponent} from '../message-modal/message-modal.component';
import {DeviceInformation} from '@mpt-fo/common/shared/models/device.model';
import {StringHelper} from '@mpt-fo/common/core/helpers/strings.helper';
import {LoginRequestModel, OtpLoginResponse} from '@mpt-fo/common/shared/models/login.model';
import {ApplicationRole} from '@mpt-fo/common/shared/models/role.model';
import {ApplicationRoles} from '@mpt-fo/common/shared/constants';
import {MoniepointAppParameters} from'@mpt-fo/common/core/models/login.model';
import {ScreenManagerService} from '@mpt-fo/common/core/services/screen-manager.service';
import {Datalayer, initializeDatalayer, updateDatalayerIdentifyUser} from '@mpt-fo/common/shared/models/data.layer.model';
import {ErrorCode} from '@app/channels/onboarding/onboarding-v2/model/onboarding-v2-model';
import { CONFIG } from '@mpt-fo/common/shared/config/config';

@Component({
	selector: 'app-web-login',
	templateUrl: './web-login.component.html',
	styleUrls: ['./web-login.component.scss'],
})
export class WebLoginComponent implements OnInit {
	validateForm!: FormGroup;
	passwordVisible = false;
	submitting: boolean;
	userRole;
	roles: ApplicationRole[] = ApplicationRoles;
	token: any;
	userType: any;
	currentView: any;
	otpResponse: any;
	timer: number;
	countDownTimer;
	countDown = false;
	otp: any;
	tokenData: OtpLoginResponse;
	otpForm: FormGroup;
	ussdCode: any;
	identifier: any;
	consolidationExpiryDate: any;
	hasClaimRequest: boolean;
	hasDuplicatedProfile: boolean;
	businesses: any[] = [];
	runningModal: any;
	readonly ROUTE = '/login/web';
	suspensionReleaseTime: any;
	isInitiated = false;
	readonly NETWORK_ERROR = 'Please make sure you have internet connectivity';
	webBicon;

	constructor(
		public fb: FormBuilder,
		public router: Router,
		public config: AppConfig,
		public auth: AuthenticationService,
		public localStorage: LocalStorageService,
		public user: AuthorizedUser,
		public deviceService: DeviceService,
		public complianceService: ComplianceService,
		public modal: ModalService,
		public activatedRoute: ActivatedRoute,
		public mobileService: MobileWebViewService,
		public delegateService: MobileDelegateService,
		public kycService: KycService,
		public loginHelper: LoginHelperService,
		public modalHelper: ModalHelperService,
		public deviceHelper: DeviceHelperService,
		public modalService: ModalService,
		public dataLayerHelper: DataLayerService,
		public screenManager: ScreenManagerService
	) {
	}

	ngOnInit(): void {
		this.initializeLogin();
	}

	initializeLogin(){
		this.webBicon = this.config.WEB_BICON;
		this.setForms();
		this.handleScreenMangerChange();
	}

	handleScreenMangerChange(){
		this.screenManager.isMobile$.subscribe((isMobile) => {
			const isContainWebLogin = this.router?.url.includes(this.ROUTE);
			const canRoute = isContainWebLogin && isMobile;
			if (canRoute) {
				this.getLoginParameters();
			}
		});
	}

	getLoginParameters() {
		const loginParameters: MoniepointAppParameters = this.mobileService.getMoniepointAppParameters();
		this.mobileService.saveMoniepointAppParameters(loginParameters);
		const username = loginParameters?.deviceRegistrationRequest?.username;
		const isSlashScreen = StringHelper.isNullOrEmptyOrUndefined(username) && this.mobileService.isMobileWebViewUser();
		const route = isSlashScreen ? 'mobile/splash-screen' : 'mobile';
		this.router.navigateByUrl(`login/${route}`);
	}

	async handleOncloseMessageModal(param) {
		await this.handleOncloseMessageModalIfMessageTypeComplete(param?.messageType, param?.res);
		await this.handleOncloseMessageModalIfMessageTypeWarning(param?.messageType);
		if (this.runningModal) {
			this.runningModal.destroy();
		}
	}

	async handleOncloseMessageModalIfMessageTypeWarning(messageType){
		if(messageType !== MessageType.WARNING){
			return;
		}
		this.loginHelper.setLoginOtpResponse(this.otpResponse);
		await this.router.navigateByUrl('/login/web/device-registration');
	}

	async handleOncloseMessageModalIfMessageTypeComplete(messageType, res){
		if(messageType !== MessageType.COMPLETE){
			return;
		}
		const deviceInformation = this.getDeviceInformation();
		await this.loginHelper.processSessionUser(res, deviceInformation);
	}


	createModalConfig(params) {
		const config = new ModalOptionsConfig();
		config.nzWidth = 396;
		config.nzComponentParams.data = params;
		config.nzComponentParams.callback = async (result) => {
			await this.handleOncloseMessageModal(result);
		};
		return config;
	}

	launchMessageModal(params) {
		const config = this.createModalConfig(params);
		this.runningModal = this.modal.createModal(MessageModalComponent, config);
	}

	launchSuccessModal(res) {
		const config = new ModalOptionsConfig();
		config.nzComponentParams.alertType = 'success';
		config.nzComponentParams.callback = () => this.loginHelper.processSessionUser(res, this.deviceHelper.getDeviceInformation());
		config.nzComponentParams.message = 'Device was successfully registered';
		this.modal.alert(config);
	}

	back() {
		this.currentView = null;
	}

	async buildLoginRequest(request: LoginRequestModel) {
		const deviceInformation = this.getDeviceInformation();
		this.updateRequestIdWithDeviceInformation(request, deviceInformation);
		request.loginType = 'PASSWORD';
		return request;
	}

	updateRequestIdWithDeviceInformation(request: LoginRequestModel, deviceInformation: DeviceInformation){
		request.deviceUniqueIdentifier = deviceInformation?.deviceUniqueIdentifier;
		request.manufacturer = deviceInformation?.manufacturer;
		request.model = deviceInformation?.model;
	}

	getDeviceInformation(){
		return this.deviceHelper.getDeviceInformation();
	}

	storeUserLoginCredentials(username, password){
		this.user.setLoginCredentials({username, password});
	}

	handleLoginRequestFailure(err, username){
		const isNetworkError = !window?.navigator?.onLine;
		isNetworkError ? this.handleNetworkError(username)
		: this.handleNonNetworkError(err, username);
	}

	async handleNonNetworkError(err, username){
		const isNewDeviceDetected = err && (err?.error?.code === '403003' || err?.error?.code === ErrorCode.LIVELINESS_REQUIRED);
		isNewDeviceDetected ? this.registerNewDevice(err)
		: this.handleNonDeviceRegistrationError(err, username);
	}

	handleNonDeviceRegistrationError(err, username){
		const isOtpLockout = err && err?.error?.code === '400209';
		const params = {username: username, err};
		isOtpLockout ? this.otpLockout(err) : this.loginFailed(params);
		this.submitting = false;
	}

	async login(request: LoginRequestModel) {
		this.submitting = true;
		request = await this.buildLoginRequest(request);
		this.storeUserLoginCredentials(request?.username, request?.password);
		const response = await this.auth.loginFD(request);
		await this.handleLoginResponse(response, request?.username);
	}

	async handleLoginResponse(res, username){
		this.handleLoginResponseEvent(res, username);
		const deviceInformation = this.getDeviceInformation();
		await this.loginHelper.processSessionUser(res, deviceInformation);
	}

	handleLoginResponseEvent(res, username){
		const payload = this.getDataLayer('sign_in_success',username, res);
		const payload1 = this.getDataLayer('identify_user', username, res);
		this.dataLayerHelper.logEvent(payload);
		this.dataLayerHelper.logEvent(payload1);
	}

	handleNetworkError(username) {
		const description = this.NETWORK_ERROR;
		const params = {description, username};
		this.networkErrorEvent(params);
		const modalOptions: ModalOptions = this.getNetworkErrorParams();
		this.modalHelper.alert(modalOptions);
	}

	getNetworkErrorParams(): ModalOptions {
		return {
			title: 'No Connectivity',
			description: this.NETWORK_ERROR,
			alertType: 'error',
			callbackName: 'Close',
		};
	}

	networkErrorEvent(params: any) {
		const payload = this.getDataLayer('sign_in_failed', params?.username, params?.description);
		payload[`api_error_message`] = params?.description;
		payload[`api_error_code`] = '';
		this.dataLayerHelper.logEvent(payload);
	}

	registerNewDevice(params) {
		this.otpResponse = params?.error;
		this.identifier = params?.error?.identifier;
		this.tokenData = params?.error?.tokenData;
		this.ussdCode = this.otpResponse?.ussdCode;
		const deviceRegistrationParameters = this.getDeviceRegistrationParameters();
		this.launchMessageModal(deviceRegistrationParameters);
	}

	getDeviceRegistrationParameters(){
		return {
			title: 'New Device Detected',
			description: 'Your login was successful but you need to register this device.',
			messageType: MessageType.WARNING,
			useFullButton: true,
			forNewDevice: true,
			buttonText: 'Register Device'
		}
	}

	loginFailedEvent(params) {
		const description = this.getFailedLoginEventDescription(params);
		const payload = this.getDataLayer('sign_in_failed', params?.username, description);
		payload[`api_error_message`] = StringHelper.getMixpanelErrorMessage(params?.err);
		payload[`api_error_code`] = params?.err?.status;
		this.dataLayerHelper.logEvent(payload);
	}

	getFailedLoginEventDescription(params){
		return params?.err?.error?.responseMessage ||
		params?.err?.error?.description ||
		StringHelper.getErrorMessageHttpErrorResponse(params?.err);
	}

	loginFailed(params) {
		this.loginFailedEvent(params);
		const modalOptions: ModalOptions = this.getFailedLoginParams(params);
		this.modalHelper.alert(modalOptions);
	}

	getFailedLoginParams(params): ModalOptions{
		return {
			title: 'Login Failed',
			description: params?.err.error?.description || 'An Error occurred',
			alertType: 'error',
			callbackName: 'Close',
		};
	}

	otpLockout(params) {
		const modalOptions: ModalOptions = this.getOtpLockoutParams(params);
		this.modalHelper.alert(modalOptions);
	}

	getOtpLockoutParams(params): ModalOptions{
		return {
			title: 'OTP Lockout',
			description: params.error?.responseMessage || params.error?.description || 'An Error occurred',
			alertType: 'error',
			expiryTime: params.error?.suspensionReleaseTime,
			callbackName: 'Retry Verification',
		};
	}

	async submitForm(){
		this.validateFormFields();
		if (this.validateForm.invalid) {
			return;
		}
		const request: LoginRequestModel = this.validateForm.value;
		this.login(request).catch(error => {this.handleLoginRequestFailure(error, request?.username);})
	}

	setOtpForm() {
		this.otpForm = this.fb.group({
			otp: [null, [Validators.required]],
		});
	}

	setLoginForm() {
		const credentials = this.user.loginCredentials;
		this.validateForm = this.fb.group({
			username: [credentials?.username || null, [Validators.required]],
			password: [null, [Validators.required]],
		});
		this.handleLoginFormChange();
	}

	handleLoginFormChange(){
		this.validateForm.get('username').valueChanges.subscribe((value) => {
			const username = this.validateForm?.value?.username;
			!this.isInitiated ? this.logEventOnChangeForm(username) : this.setInitiated(false);
		});
	}

	setInitiated(isInitiated){
		this.isInitiated = isInitiated;
	}

	logEventOnChangeForm(username){
		const payload = this.getDataLayer('sign_in_initiated', username);
		this.dataLayerHelper.logEvent(payload);
		this.setInitiated(true);
	}

	setForms() {
		this.setLoginForm();
		this.setOtpForm();
	}

	getDataLayer(event, username, message = '', data = null) {
		const payload = initializeDatalayer(event);
		payload.error_message = message;
		payload.user_name = username;
		payload.sign_in_mode = 'username and password';
		this.updateDatalayerPayloadIfEventIsIdentifyUser(payload, data, event);
		return payload;
	}

	updateDatalayerPayloadIfEventIsIdentifyUser(payload: Datalayer, data, event){
		if (event === 'identify_user') {
			payload = updateDatalayerIdentifyUser(payload);
			payload.email = data?.tokenData?.email;
			payload.business_owner_first_name = data?.tokenData?.firstName;
		}
	}

	signUp() {
		const canUseNewOnboardingFlow = this.config.USE_NEW_SIGN_UP_FLOW;
		canUseNewOnboardingFlow ? this.handleSignupNavigation()
		: this.router.navigateByUrl('/onboarding/create-account');
	}

	handleSignupNavigation(){
		this.loginHelper.initializeSignup();
		const route = `/onboarding-v2`;
		this.router.navigateByUrl(route);
	}

	private validateFormFields() {
		for (const i in this.validateForm.controls) {
			if (this.validateForm && this.validateForm.controls) {
				this.validateForm.controls[i].markAsDirty();
				this.validateForm.controls[i].updateValueAndValidity();
			}
		}
	}
}
